/**
 * Local storage persistence for data between user sessions.
 */

import { LocalStorage } from "../support/storage";
import { derived, writable } from "svelte/store";
import { get, set, type PropertyPath } from "lodash-es";

/**
 * {
 *   version: number,
 *   ...datas
 * }
 */
export const data = writable({
  // Bumping this version will reset all data
  version: 1
});

function syncData() {
  data.update(($data) => {
    const hydrated = LocalStorage.getItem("data", {});

    if ($data.version > hydrated.version) {
      // New version, reset
      return $data;
    } else {
      // Otherwise restore
      return { ...$data, ...hydrated };
    }
  });
}

export function initializePersistence() {
  syncData();

  window.addEventListener("storage", syncData);

  return data.subscribe(($data) => {
    LocalStorage.setItem("data", $data);

    return () => window.removeEventListener("storage", syncData);
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function write(keyPath: PropertyPath, value: any) {
  return data.update(($data) => {
    return set($data, keyPath, value);
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function read(keyPath: PropertyPath, fallback: any) {
  return derived(data, ($data) => {
    return get($data, keyPath, fallback);
  });
}

export function update<Type>(keyPath: PropertyPath, fn: (value: Type) => Type) {
  return data.update(($data) => {
    const before = get($data, keyPath);
    const after = fn(before);

    return set($data, keyPath, after);
  });
}
